import cx from 'classix';
import { Icon } from '../atoms';

type BadgeSizes = 'sm' | 'md' | 'lg' | 'xl';
export type PillBadgeColors =
  | 'gray'
  | 'error'
  | 'warning'
  | 'warning-100'
  | 'success-25'
  | 'success-100'
  | 'blue'
  | 'secondary-brand-50'
  | 'secondary-brand-25'
  | 'indigo'
  | 'orange'
  | 'success-500';
export type ModernBadgeColors =
  | 'error'
  | 'success'
  | 'warning'
  | 'orange'
  | 'brand'
  | 'blue-gray'
  | 'gray';

const PILL_COLOR_CLASSES: Record<PillBadgeColors, string> = {
  blue: 'bg-secondary-brand-25 border-secondary-brand-50 text-brand-500',
  'secondary-brand-50': 'bg-secondary-brand-50 border-secondary-brand-25 text-brand-600',
  error: 'bg-error-25 border-error-100 text-error-600',
  gray: 'bg-gray-50 border-gray-100',
  indigo: 'bg-secondary-brand-50 border-secondary-brand-25 text-brand-600',
  orange: 'bg-warning-25 border-accent-500',
  'secondary-brand-25': 'bg-secondary-brand-25 border-secondary-brand-25 text-gray-500',
  'success-25': 'bg-success-25 border-success-100 text-success-600',
  'success-100': 'border-success-25 bg-success-100 text-success-600',
  'success-500': 'border-success-25 bg-success-500 text-white',
  warning: 'bg-warning-50 border-accent-500 text-warning-600',
  'warning-100': 'bg-warning-50 border-warning-100 text-gray-600',
} as const;

const PILL_STROKE_CLASSES: Record<PillBadgeColors, string> = {
  blue: 'brand-500',
  'secondary-brand-50': 'brand-600',
  'secondary-brand-25': 'secondary-brand-25',
  error: 'error-500',
  gray: 'gray-300',
  indigo: 'brand-100',
  orange: 'accent-600',
  'success-25': 'success-600',
  'success-100': 'success-600',
  'success-500': 'white',
  warning: 'accent-600',
  'warning-100': 'gray-600',
} as const;

const BadgeDotIcon = (props: { color: PillBadgeColors | ModernBadgeColors }) => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      preserveAspectRatio='xMidYMid meet'
      width='6'
      height='6'
      viewBox={'0 0 24 24'}
      fill='none'
      className={cx(
        props.color === 'gray' && 'fill-gray-400',
        props.color === 'error' && 'fill-error-500',
        props.color === 'warning' && 'fill-warning-500',
        props.color === 'success-500' && 'fill-white',
        props.color === 'success-25' && 'fill-success-500',
        props.color === 'indigo' && 'fill-brand-100',
        props.color === 'orange' && 'fill-accent-600',
        props.color === 'blue-gray' && 'fill-brand-600',
        (props.color === 'blue' || props.color === 'brand') && 'fill-brand-500',
      )}
    >
      <path
        d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

const BadgeCloseIcon = (props: {
  color?: PillBadgeColors;
  onClick: () => void;
  isModern: boolean;
}) => {
  const color = props.color ? props.color : 'gray';
  return (
    <span onClick={props.onClick} className='p-[2px] hover:cursor-pointer'>
      <Icon
        type='x-close'
        size='13'
        strokeWidth='2.75px'
        stroke={props.isModern ? 'gray-300' : PILL_STROKE_CLASSES[color]}
      />
    </span>
  );
};

export type PillProps =
  | {
      variant: 'plain' | 'dot';
      color: PillBadgeColors;
      size: BadgeSizes;
      children?: React.ReactNode;
      additionalClassnames?: string;
    }
  | {
      variant: 'close';
      color: PillBadgeColors;
      size: BadgeSizes;
      children?: React.ReactNode;
      onClick: () => void;
      additionalClassnames?: string;
    };

export const PillBadge = (props: PillProps) => {
  const isSmall = props.size === 'sm';
  const isMedium = props.size === 'md';
  const isLarge = props.size === 'lg';
  const isXL = props.size === 'xl';

  return (
    <span
      className={cx(
        'inline-flex items-center justify-center font-medium border rounded-2xl whitespace-nowrap',
        isSmall ? 'h-[22px] text-xs min-w-[22px]' : 'text-sm',
        isMedium && 'h-6 min-w-6',
        isLarge ? 'h-7 min-w-7 py-1' : 'py-0.5',
        ['gray', 'orange'].includes(props.color) && 'text-gray-500',
        props.variant === 'close' && 'gap-x-0.5',
        props.variant === 'dot' && (isSmall ? 'gap-x-1' : 'gap-x-1.5'),
        props.variant === 'plain' && isSmall && 'px-2',
        props.variant === 'plain' && isMedium && 'px-2.5',
        props.variant === 'plain' && isLarge && 'px-3',
        props.variant === 'dot' && isSmall && 'pr-2 pl-1.5',
        props.variant === 'dot' && isMedium && 'pr-2.5 pl-2',
        props.variant === 'dot' && isLarge && 'pr-3 pl-2.5',
        props.variant === 'close' && isSmall && 'pr-[3px] pl-2',
        props.variant === 'close' && isMedium && 'pr-1 pl-2.5',
        props.variant === 'close' && isLarge && 'pr-1.5 pl-3',
        props.variant === 'close' && isXL && 'pr-1.5 pl-3 py-1',
        PILL_COLOR_CLASSES[props.color],
        props.additionalClassnames,
      )}
    >
      {props.variant === 'dot' && <BadgeDotIcon color={props.color} />}
      <div className='text-nowrap'>{props.children}</div>
      {props.variant === 'close' && (
        <BadgeCloseIcon isModern={false} color={props.color} onClick={props.onClick} />
      )}
    </span>
  );
};

export const ModernBadge = (
  props:
    | {
        variant: 'plain';
        size: BadgeSizes;
        children: React.ReactNode;
      }
    | {
        variant: 'close';
        size: BadgeSizes;
        children: React.ReactNode;
        onClick: () => void;
      }
    | {
        variant: 'dot';
        size: BadgeSizes;
        children: React.ReactNode;
        color: ModernBadgeColors;
      },
) => {
  const isSmall = props.size === 'sm';
  const isMedium = props.size === 'md';
  const isLarge = props.size === 'lg';
  return (
    <span
      className={cx(
        'inline-flex items-center justify-center font-medium border rounded-md shadow-xs border-gray-200 text-gray-500',
        isSmall ? 'h-[22px] text-xs min-w-[22px]' : 'text-sm',
        isMedium && 'h-6 min-w-6',
        isLarge ? 'h-7 min-w-7 py-1' : 'py-0.5',
        props.variant === 'close' && 'gap-x-0.5',
        props.variant === 'dot' && (isSmall ? 'gap-x-1' : 'gap-x-1.5'),
        isSmall && (props.variant === 'close' ? 'pr-[3px] pl-1.5' : 'px-1.5'),
        isMedium && (props.variant === 'close' ? 'pr-1 pl-2' : 'px-2'),
        isLarge && (props.variant === 'close' ? 'pr-1.5 pl-2.5' : 'px-2.5'),
      )}
    >
      {props.variant === 'dot' && <BadgeDotIcon color={props.color} />}
      {props.children}
      {props.variant === 'close' && <BadgeCloseIcon isModern={true} onClick={props.onClick} />}
    </span>
  );
};

export const PlusButton = (
  props:
    | {
        variant: 'pill';
        size: BadgeSizes;
        color: PillBadgeColors;
        onClick: () => void;
      }
    | {
        variant: 'modern';
        size: BadgeSizes;
        onClick: () => void;
      },
) => {
  return (
    <span
      onClick={props.onClick}
      className={cx(
        'inline-flex items-center justify-center border hover:cursor-pointer',
        props.variant === 'pill' ? 'rounded-[50%]' : 'rounded-md shadow-xs',
        props.size === 'sm' && 'h-[22px] w-[22px]',
        props.size === 'md' && 'h-6 w-6',
        props.size === 'lg' && 'h-7 w-7',
        props.variant === 'pill' && PILL_COLOR_CLASSES[props.color],
      )}
    >
      <Icon
        type='plus'
        size='13'
        strokeWidth='2.5px'
        stroke={props.variant === 'modern' ? 'gray-300' : PILL_STROKE_CLASSES[props.color]}
      />
    </span>
  );
};

export const NumericBadge = (props: {
  size: BadgeSizes;
  color: PillBadgeColors;
  onClick?: () => void;
  number: number;
}) => {
  return (
    <span
      onClick={props.onClick}
      className={cx(
        'inline-flex items-center justify-center border hover:cursor-pointer',
        'rounded-[50%]',
        props.size === 'sm' && 'h-[22px] w-[22px]',
        props.size === 'md' && 'h-6 w-6',
        props.size === 'lg' && 'h-7 w-7',
        'text-xs',
        PILL_COLOR_CLASSES[props.color],
      )}
    >
      {props.number}
    </span>
  );
};
